"use client";

import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { motion, AnimatePresence } from "motion/react";
import { Upload, X, FileText, ImageIcon } from "lucide-react";
import type { FileUploadProps } from "@/types/contact";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ACCEPTED_TYPES: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

export function FileUpload({
  onFileSelect,
  selectedFile,
  error,
}: FileUploadProps) {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [dropError, setDropError] = React.useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setDropError(null);

      if (rejectedFiles.length > 0) {
        const firstError = rejectedFiles[0].errors[0];
        if (firstError.code === "file-too-large") {
          setDropError("File size exceeds 5MB limit.");
        } else if (firstError.code === "file-invalid-type") {
          setDropError("Invalid file type. Accepted: JPG, PNG, WebP, PDF.");
        } else {
          setDropError("File could not be uploaded.");
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        if (isImageFile(file)) {
          setPreviewUrl(URL.createObjectURL(file));
        } else {
          setPreviewUrl(null);
        }
        onFileSelect(file);
      }
    },
    [onFileSelect, previewUrl]
  );

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setDropError(null);
    onFileSelect(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const displayError = dropError ?? error;

  // Resolve dropzone border color based on state — no shorthand mixing
  const dropzoneBorderColor: string = isDragActive
    ? "#C5A880"
    : displayError
    ? "rgba(239,68,68,0.5)"
    : "rgba(197,168,128,0.25)";

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-xs font-bold uppercase tracking-widest"
        style={{ color: "#C5A880" }}
      >
        Reference File
        <span
          className="ml-2 text-xs font-normal normal-case tracking-normal"
          style={{ color: "rgba(197,168,128,0.6)" }}
        >
          (Optional)
        </span>
      </label>

      <AnimatePresence mode="wait">
        {selectedFile ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="relative rounded-xl overflow-hidden"
            style={{
              // Fix: separate border props — no shorthand
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "rgba(197,168,128,0.3)",
              background: "rgba(197,168,128,0.05)",
            }}
          >
            {previewUrl ? (
              <div className="relative">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={previewUrl}
                  alt="Uploaded preview"
                  className="w-full object-cover rounded-xl"
                  style={{ maxHeight: "180px" }}
                />
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)",
                  }}
                />
                <div className="absolute bottom-3 left-3 right-10">
                  <p
                    className="text-xs font-semibold truncate"
                    style={{ color: "#ffffff" }}
                  >
                    {selectedFile.name}
                  </p>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0"
                  style={{ background: "rgba(197,168,128,0.15)" }}
                >
                  <FileText size={20} style={{ color: "#C5A880" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-semibold truncate"
                    style={{ color: "#e5e7eb" }}
                  >
                    {selectedFile.name}
                  </p>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: "rgba(229,231,235,0.5)" }}
                  >
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
            )}

            <motion.button
              type="button"
              onClick={handleRemove}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute top-2.5 right-2.5 flex items-center justify-center w-7 h-7 rounded-full z-10"
              style={{
                background: "rgba(239,68,68,0.15)",
                // Fix: separate border props on remove button
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "rgba(239,68,68,0.3)",
              }}
              aria-label="Remove uploaded file"
            >
              <X size={14} style={{ color: "#ef4444" }} />
            </motion.button>
          </motion.div>
        ) : (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25 }}
          >
            <div
              {...getRootProps()}
              className="flex flex-col items-center justify-center gap-3 rounded-xl p-8 cursor-pointer outline-none transition-colors duration-300"
              style={{
                // Fix: use borderWidth + borderStyle + borderColor
                // instead of border shorthand mixed with borderColor override
                borderWidth: "2px",
                borderStyle: "dashed",
                borderColor: dropzoneBorderColor,
                background: isDragActive
                  ? "rgba(197,168,128,0.08)"
                  : "rgba(197,168,128,0.03)",
              }}
              role="button"
              tabIndex={0}
              aria-label="Upload reference file. Accepted formats: JPG, PNG, WebP, PDF. Maximum size 5MB."
            >
              <input {...getInputProps()} />

              <motion.div
                animate={{ scale: isDragActive ? 1.15 : 1 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center w-12 h-12 rounded-full"
                style={{ background: "rgba(197,168,128,0.12)" }}
                aria-hidden="true"
              >
                {isDragActive ? (
                  <ImageIcon size={22} style={{ color: "#C5A880" }} />
                ) : (
                  <Upload size={22} style={{ color: "#C5A880" }} />
                )}
              </motion.div>

              <div className="text-center pointer-events-none">
                <p
                  className="text-sm font-semibold"
                  style={{ color: "#e5e7eb" }}
                >
                  {isDragActive
                    ? "Drop your file here"
                    : "Drag & drop or click to upload"}
                </p>
                <p
                  className="text-xs mt-1"
                  style={{ color: "rgba(229,231,235,0.4)" }}
                >
                  JPG, PNG, WebP, PDF — Max 5MB
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {displayError && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="text-xs font-medium mt-0.5"
            style={{ color: "#ef4444" }}
            role="alert"
            aria-live="polite"
          >
            {displayError}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}